import { Grid, Typography } from '@material-ui/core';
import { Button, Card, Dialog, i18n, ToggleButton } from '@nutrien/cxp-components';
import DOMPurify from 'isomorphic-dompurify';
import React, { useEffect, useMemo, useState } from 'react';
import { useRxCollection, useRxData } from 'rxdb-hooks';

import GenericLocationComponent from '@/components/GenericLocationComponent';
import { ConditionColor } from '@/utilities/constants';
import useModalStyles from '@/utilities/modalStyles';

import { GroundHazardCollection, GroundHazardDocument } from '../../rxdb/GroundHazard/queryBuilder';
import { GroundHazardConditionType } from '../../rxdb/HazardConditionType/queryBuilder';
import {
  InspectionDetails,
  InspectionOptionsWithDetails,
} from '../../rxdb/Inspections/useInspections';
import useLocations, { PopulatedLocation } from '../../rxdb/Locations/useLocations';
import RxdbCollectionName from '../../rxdb/rxdbCollectionName';
import { useDateFormatters } from '../../utilities';
import { formatAsStartCase } from '../../utilities/utilityFunctions';
import { AreaCheck } from '../AddAreaCheck/AddAreaCheck';
import ChecksThisShiftSignatureCard from '../ChecksThisShiftSignatureCard';
import HazardPanelContent from '../HazardPanelContent';
import LocationDetailsComponent from '../LocationDetailsComponent';
import SmartToggleButtonGroup from '../SmartToggleButtonGroup';
import useStyles from './AreaCheckReadOnlyModal.styles';

interface Props {
  open: boolean;
  onClose: () => void;
  inspectionDetailsSource: InspectionDetails;
  selectedOptionsSource: Record<string, InspectionOptionsWithDetails>;
  areaChecks: AreaCheck[];
  signatureId: string;
  createdOn: string;
  inspectionLocation?: PopulatedLocation | null;
}

const AreaCheckReadOnlyModal: React.FC<Props> = ({
  open,
  onClose,
  inspectionDetailsSource,
  selectedOptionsSource = {},
  areaChecks,
  signatureId,
  createdOn,
  inspectionLocation,
}: Props) => {
  const classes = useStyles();
  const modalClasses = useModalStyles();
  const { formatDateWithDisappearingDate } = useDateFormatters();
  const { getLocations, locationsInitialized } = useLocations();

  const [enrichedHazards, setEnrichedHazards] = useState({});
  const [enrichedLocations, setEnrichedLocations] = useState({});

  const [hazardsLoaded, setHazardsLoaded] = useState<boolean>(false);
  const areaCheckName = useMemo(
    () => formatAsStartCase(inspectionDetailsSource?.inspection?.description),
    [inspectionDetailsSource],
  );

  // collections
  const groundHazardCollection: GroundHazardCollection = useRxCollection(
    RxdbCollectionName.GROUND_HAZARDS,
  );

  const fetchGroundHazard = async (id: string) => {
    const gc = await groundHazardCollection.findOne().where('id').eq(id).exec();
    return gc;
  };

  const conditionTypesQueryConstructor = (collection: any) => {
    return collection.find();
  };

  const { result: conditionTypes }: { result: GroundHazardConditionType[] } = useRxData(
    RxdbCollectionName.GROUND_HAZARD_CONDITION_TYPES,
    conditionTypesQueryConstructor,
  );

  const getConditionType = (conditionTypeId: string) => {
    return conditionTypes?.find(c => c.id === conditionTypeId)?.displayName || '';
  };

  const getToggleButtonClass = (color: string) => {
    switch (color?.toLowerCase()) {
      case ConditionColor.GREEN:
        return classes.greenButton;
      case ConditionColor.YELLOW:
        return classes.yellowButton;
      case ConditionColor.ORANGE:
        return classes.orangeButton;
      case ConditionColor.RED:
        return classes.redButton;
      case ConditionColor.WHITE:
        return classes.whiteButton;
      default:
        return classes.greenButton;
    }
  };

  // Configure
  const setup = async () => {
    const locations = await getLocations();

    try {
      if (areaChecks) {
        const tempEnrichedHazards = {};
        const tempEnrichedLocations = {};
        for (let k = 0; k < areaChecks?.length || 0; k += 1) {
          const hazardDocuments: GroundHazardDocument[] = [];
          for (let j = 0; j < areaChecks[k].hazards.length; j += 1) {
            hazardDocuments.push(await fetchGroundHazard(areaChecks[k].hazards[j]));
          }

          tempEnrichedHazards[areaChecks[k].id] = hazardDocuments;
          if (hazardDocuments.length > 0) {
            tempEnrichedLocations[areaChecks[k].id] = locations.find(
              x => x.id === hazardDocuments[0].startLocation,
            );
          }
          setEnrichedHazards(tempEnrichedHazards);
          setEnrichedLocations(tempEnrichedLocations);
        }
        setHazardsLoaded(true);
      }
    } catch (error) {
      console.log('🚀 ~ file: AreaCheckReadOnlyModal.tsx ~ line 129 ~ setup ~ error', error);
    }
  };

  useEffect(() => {
    if (open === true && groundHazardCollection && locationsInitialized) {
      setup();
    }
  }, [open, groundHazardCollection, locationsInitialized, areaChecks]);

  const listHazards = (areaCheckId: string) => {
    const hazards = enrichedHazards[areaCheckId];
    const locationData = enrichedLocations[areaCheckId];
    if (!hazards || Object.keys(hazards).length === 0) {
      return (
        <Grid item container xs={12}>
          &nbsp;
        </Grid>
      );
    }

    return (
      <>
        {hazards.map(hazard => (
          <Grid item xs={12} className={classes.commentsContainer} key={`${hazard.id}-GridItem`}>
            {hazards.length > 0 && locationData && (
              <Grid item className={classes.locationDetails}>
                <div className={classes.locationDetailsHeader}>
                  <Typography variant="h5">{getConditionType(hazard.conditionType)}</Typography>
                  <span className={classes.dot}>&#183;</span>
                  <Typography className={classes.secondaryText} variant="h5">
                    {hazard.inspectionId ? i18n.t('Area Check') : i18n.t('Hazard')}
                  </Typography>
                </div>
                <LocationDetailsComponent location={locationData} hazardLandmark={null} />
              </Grid>
            )}
            <HazardPanelContent
              groundHazard={hazard}
              key={`${hazard.id}-HazardPanelContent`}
              hideAddPhotoAction
            />
          </Grid>
        ))}
      </>
    );
  };

  // Generate the complicated set of options based on hazard details
  const generateOptions = () => {
    if (!areaChecks || !hazardsLoaded) return [];

    return areaChecks
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(areaCheck => {
        const categoryInfo = inspectionDetailsSource?.categories.find(
          x => x.category.id === areaCheck.categoryId,
        );
        return (
          <Grid
            container
            key={`${areaCheck.id}-${areaCheck.categoryId}-${areaCheck.displayOrder}-root`}
          >
            <Grid item xs={12} key={`${areaCheck.id}-${areaCheck.displayOrder}-parent-Grid`}>
              <Card className={classes.card}>
                <Grid container>
                  <Grid container item xs={12}>
                    <Grid item>
                      <Typography variant="h3" className={classes.categoryTitle}>
                        {categoryInfo?.category.description || ''}
                      </Typography>
                    </Grid>
                  </Grid>
                  {categoryInfo?.category?.detail && (
                    <Grid item xs={12}>
                      <Typography variant="body2" className={classes.card}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(categoryInfo?.category?.detail),
                          }}
                        />
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} className={classes.buttonGroupContainer}>
                    <SmartToggleButtonGroup
                      id={`${areaCheck.id}-${areaCheck.categoryId}-ToggleButtonGroup-id`}
                      key={`${areaCheck.id}-${areaCheck.categoryId}-ToggleButtonGroup-key`}
                      value={selectedOptionsSource[areaCheck.id].id || null}
                      onChange={() => {}}
                      aria-label=""
                      exclusive
                      viewOnly
                      list={inspectionDetailsSource.categories
                        .find(x => x.category.id === areaCheck.categoryId)
                        ?.options.sort((a, b) => a.option.displayOrder - b.option.displayOrder)}
                      dropdownClassName={classes.dropdown}
                      dropdownGetValue={(val: any) => {
                        const option = inspectionDetailsSource.categories
                          .find(x => x.category.id === areaCheck.categoryId)
                          ?.options.find(y => y.option.id === val);
                        return option;
                      }}
                      dropDownOptionLabel={(item: any) => {
                        if (item === '') {
                          return '';
                        }
                        return item.option.description;
                      }}
                    >
                      {inspectionDetailsSource.categories
                        .find(x => x.category.id === areaCheck.categoryId)
                        ?.options.sort((a, b) => a.option.displayOrder - b.option.displayOrder)
                        .map(val => {
                          const { option } = val;
                          return (
                            <ToggleButton
                              key={`${areaCheck.id}+${areaCheck.categoryId}+${option.id}-${areaCheck.displayOrder}`}
                              {...(option.color
                                ? { classes: { selected: getToggleButtonClass(option.color) } }
                                : {
                                    // Handle legacy color logic if `option.color` is not provided
                                    color:
                                      val.hazardCondition ||
                                      option?.description?.toUpperCase() === 'DEFICIENCY'
                                        ? 'error'
                                        : 'success',
                                  })}
                              value={option.id}
                              aria-label={`${option.id}-toggle`}
                              disabled
                              showColorOnDisabled
                              disableRipple
                            >
                              <Typography variant="button">{option.description}</Typography>
                            </ToggleButton>
                          );
                        })}
                    </SmartToggleButtonGroup>
                  </Grid>
                  {areaCheck.hazards.length > 0 && hazardsLoaded && (
                    <>{listHazards(areaCheck.id)}</>
                  )}
                </Grid>
              </Card>
            </Grid>
          </Grid>
        );
      });
  };

  return (
    <>
      {open && (
        <Dialog
          data-cy="AreaCheckReadOnlyModal"
          open={open}
          onClose={onClose}
          titleText={areaCheckName}
          backButton={false}
          customLeftHeaderButton={
            <div className={classes.createdDate}>
              <Typography variant="h5" color="primary">
                {formatDateWithDisappearingDate(createdOn)}
              </Typography>
            </div>
          }
          fullScreen
          disableEnforceFocus
          dialogActions={
            <Grid container spacing={2} justify="space-between">
              <Grid item />
              <Grid item>
                <Button
                  onClick={onClose}
                  variant="contained"
                  color="primary"
                  noMinHeight
                  id="area-check-read-only-modal-close-button"
                >
                  {i18n.t('Close')}
                </Button>
              </Grid>
            </Grid>
          }
          BackdropProps={{
            onClick: event => {
              event.preventDefault();
            },
            onTouchStart: event => {
              event.preventDefault();
            },
            style: { touchAction: 'none' },
          }}
        >
          {inspectionLocation && (
            <Card className={modalClasses.card}>
              <Typography variant="h3">Location</Typography>
              <GenericLocationComponent
                location={inspectionLocation}
                onLocationChange={() => {}}
                setHasValidationErrors={() => {}}
                viewOnly={true}
              />
            </Card>
          )}
          <Grid container>{generateOptions()}</Grid>
          <Grid container>
            <ChecksThisShiftSignatureCard signatureId={signatureId} />
          </Grid>
        </Dialog>
      )}
    </>
  );
};

export default AreaCheckReadOnlyModal;
